import { logDictionaryLoadError } from './loggingUtils.ts';

export interface DictionaryConfig {
  type: string;
  version: number;
  patch: number;
}

export const TWL06_BASE_DICTIONARY_CONFIG: DictionaryConfig = {
  type: 'twl06',
  version: 1,
  patch: 0,
};

export const SCOWL60_BASE_DICTIONARY_CONFIG: DictionaryConfig = {
  type: 'scowl60',
  version: 1,
  patch: 1,
};

function getDictionaryBaseUrl(type: string, version: number) {
  return `/dictionaries/${type}/v${version}-${type}-base.json`;
}

function getDictionaryPatchUrl(type: string, version: number, patchNum: number) {
  return `/dictionaries/${type}/v${version}-${type}-patch-${String(patchNum).padStart(3, '0')}.json`;
}

// In-memory cache for loaded dictionaries
const dictionaryCache: { [key: string]: Set<string> } = {};

function getDictionaryCacheKey(type: string, version: number, patch: number): string {
  return `${type}|${version}|${patch}`;
}

/**
 * Fetches a URL with retry logic and timeout handling.
 * Retries up to maxRetries times with exponential backoff.
 * Uses AbortController for timeout on each attempt.
 */
async function fetchWithRetry(
  url: string,
  options: {
    maxRetries?: number;
    timeoutMs?: number;
    initialDelayMs?: number;
  } = {}
): Promise<Response> {
  const { maxRetries = 3, timeoutMs = 30000, initialDelayMs = 1000 } = options;

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Wait before retry (exponential backoff)
    if (attempt > 0) {
      const delay = initialDelayMs * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (response.ok) {
        return response;
      }

      // Don't retry on 4xx errors (client errors)
      if (response.status >= 400 && response.status < 500) {
        throw new Error(`HTTP ${response.status}: ${url}`);
      }

      lastError = new Error(`HTTP ${response.status}: ${url}`);
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        // Check if it's an abort error (timeout)
        if (error.name === 'AbortError') {
          lastError = new Error(`Request timed out after ${timeoutMs}ms: ${url}`);
        } else {
          lastError = error;
        }
      } else {
        lastError = new Error(`Unknown error fetching ${url}`);
      }
    }
  }

  throw lastError || new Error(`Failed to fetch ${url} after ${maxRetries + 1} attempts`);
}

/**
 * Loads a dictionary asynchronously by type, version, and patch.
 * Applies all patches up to the specified patch number (inclusive).
 * Uses in-memory cache to avoid redundant loads.
 * Includes retry logic with exponential backoff for mobile network reliability.
 */
export async function loadDictionary(config: DictionaryConfig): Promise<Set<string>> {
  const type = config.type;
  const version = config.version;
  const patch = config.patch;
  const maxRetries = 3;

  const cacheKey = getDictionaryCacheKey(type, version, patch);
  if (dictionaryCache[cacheKey]) {
    return dictionaryCache[cacheKey];
  }

  try {
    // Load base dictionary with retry logic
    // Use longer timeout for base dictionary (it can be large: ~2.6MB for TWL06)
    const baseUrl = getDictionaryBaseUrl(type, version);
    const baseResp = await fetchWithRetry(baseUrl, {
      maxRetries,
      timeoutMs: 60000, // 60 seconds for large dictionary files
      initialDelayMs: 2000,
    });
    if (!baseResp.ok) throw new Error(`Dictionary base file not found: ${baseUrl}`);
    const baseWords: string[] = await baseResp.json();
    const wordSet = new Set(baseWords);

    // Apply patches (smaller files, shorter timeout)
    for (let i = 1; i <= patch; i++) {
      const patchUrl = getDictionaryPatchUrl(type, version, i);
      try {
        const patchResp = await fetchWithRetry(patchUrl, {
          maxRetries: 2,
          timeoutMs: 15000, // 15 seconds for small patch files
          initialDelayMs: 1000,
        });
        if (!patchResp.ok) continue;
        const patchData = await patchResp.json();
        if (Array.isArray(patchData.added)) {
          for (const word of patchData.added) wordSet.add(word);
        }
        if (Array.isArray(patchData.removed)) {
          for (const word of patchData.removed) wordSet.delete(word);
        }
      } catch {
        // Patches are optional, continue if one fails
        console.warn(`Failed to load dictionary patch ${i}, continuing without it`);
      }
    }
    dictionaryCache[cacheKey] = wordSet;
    return wordSet;
  } catch (error) {
    // Log the error to Google Analytics for monitoring
    const errorMessage = error instanceof Error ? error.message : String(error);
    logDictionaryLoadError(type, version, errorMessage, maxRetries);

    // Re-throw so the UI can handle the error
    throw error;
  }
} 